import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  IconButton,
  LinearProgress,
  alpha,
  Tabs,
  Tab,
  Tooltip,
  Autocomplete,
  TextField,
} from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { TenantHomeDashboardNavigationBar } from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import {
  usePlatformMigrationRuns,
  usePlatformMigrationItems,
  useTriggerPlatformMigration,
  usePlatformMigrationTenantOptions,
  usePlatformMigrationSystemOptions,
  useTriggerHealthCheck,
  useHealthCheckStatus,
  useHealthCheckItems,
  useTriggerFleetSync,
  useTriggerTenantSync,
  useSyncStatus,
  useSyncItems,
} from 'services/v1/TenantMaster/PlatformMigrationService';
import {
  PlatformMigrationRun,
  PlatformMigrationRunItem,
  PlatformMigrationRunRequest,
  HealthCheckItem,
  SyncItem,
} from 'types/api/TenantMaster/PlatformMigrationTypes';
import { dayjs } from 'utils/DateUtils';
import { ENV_CONFIG } from 'config/env';
import { getErrorMessage } from 'utils/Error';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StorageIcon from '@mui/icons-material/Storage';
import DnsIcon from '@mui/icons-material/Dns';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import SyncIcon from '@mui/icons-material/Sync';
import VerifiedIcon from '@mui/icons-material/Verified';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SyncLockIcon from '@mui/icons-material/SyncLock';

// ── Shared constants & helpers ──

// Database groups that support optional ID-level scoping in the trigger dialog.
const TENANT_DATABASES_GROUP = 'TENANT_DATABASES';
const SYSTEM_DATABASES_GROUP = 'SYSTEM_DATABASES';

type ScopeOption = { id: string; label: string };

const DATABASE_GROUP_OPTIONS: { value: string; label: string; description: string; icon: React.ReactNode }[] = [
  {
    value: 'TENANT_MASTER',
    label: 'Tenant Master',
    description: 'Main tenant configuration database',
    icon: <StorageIcon sx={{ fontSize: 20, color: '#3B4797' }} />,
  },
  {
    value: 'SYSTEM_MASTER',
    label: 'System Master',
    description: 'Core system configuration database',
    icon: <StorageIcon sx={{ fontSize: 20, color: '#42BB93' }} />,
  },
  {
    value: 'SYSTEM_DATABASES',
    label: 'System Databases',
    description: 'All system-level databases',
    icon: <DnsIcon sx={{ fontSize: 20, color: '#00B3DC' }} />,
  },
  {
    value: 'TENANT_DATABASES',
    label: 'Tenant Databases',
    description: 'All tenant-level databases',
    icon: <DnsIcon sx={{ fontSize: 20, color: '#E8833A' }} />,
  },
];

const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: React.ReactNode }> = {
  COMPLETED: { color: '#42BB93', bg: '#E8F8F2', icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> },
  COMPLETED_WITH_FAILURES: { color: '#E8833A', bg: '#FFF3E8', icon: <WarningAmberIcon sx={{ fontSize: 16 }} /> },
  FAILED: { color: '#BB4942', bg: '#FDECEB', icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} /> },
  RUNNING: { color: '#3B4797', bg: '#ECEEF8', icon: <PlayCircleOutlineIcon sx={{ fontSize: 16 }} /> },
  QUEUED: { color: '#00B3DC', bg: '#E5F7FC', icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} /> },
  PENDING: { color: '#828C99', bg: '#F3F6F9', icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} /> },
  HEALTHY: { color: '#42BB93', bg: '#E8F8F2', icon: <VerifiedIcon sx={{ fontSize: 16 }} /> },
  REPAIRABLE_CHECKSUM_ONLY: { color: '#00B3DC', bg: '#E5F7FC', icon: <BuildCircleIcon sx={{ fontSize: 16 }} /> },
  REPAIRABLE_PATCH_AND_CHECKSUM: { color: '#E8833A', bg: '#FFF3E8', icon: <BuildCircleIcon sx={{ fontSize: 16 }} /> },
  NEEDS_MANUAL_REVIEW: { color: '#BB4942', bg: '#FDECEB', icon: <VisibilityIcon sx={{ fontSize: 16 }} /> },
  UNREACHABLE: { color: '#6B7280', bg: '#F3F4F6', icon: <SignalWifiOffIcon sx={{ fontSize: 16 }} /> },
  PATCHING: { color: '#3B4797', bg: '#ECEEF8', icon: <BuildCircleIcon sx={{ fontSize: 16 }} /> },
  CHECKSUM_RESET: { color: '#00B3DC', bg: '#E5F7FC', icon: <SyncLockIcon sx={{ fontSize: 16 }} /> },
  LIQUIBASE_RUNNING: { color: '#3B4797', bg: '#ECEEF8', icon: <PlayCircleOutlineIcon sx={{ fontSize: 16 }} /> },
  SKIPPED: { color: '#828C99', bg: '#F3F6F9', icon: <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} /> },
};

function getStatusConfig(status: string) {
  return STATUS_CONFIG[status] || { color: '#828C99', bg: '#F3F6F9', icon: null };
}

function StatusChip({ status }: { status: string }) {
  const config = getStatusConfig(status);
  return (
    <Chip
      label={status.replace(/_/g, ' ')}
      size='small'
      icon={config.icon as React.ReactElement}
      sx={{
        bgcolor: config.bg,
        color: config.color,
        fontWeight: 600,
        fontSize: 11,
        borderRadius: 1,
        '& .MuiChip-icon': { color: config.color },
      }}
    />
  );
}

function SummaryCard(props: { label: string; value: number; color: string; bg: string; icon: React.ReactNode }) {
  return (
    <Paper
      variant='outlined'
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 2,
        borderColor: alpha(props.color, 0.2),
        bgcolor: props.bg,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: `0 2px 8px ${alpha(props.color, 0.15)}` },
      }}
    >
      <Stack direction='row' alignItems='center' spacing={1.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: alpha(props.color, 0.12),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: props.color,
          }}
        >
          {props.icon}
        </Box>
        <Box>
          <Typography variant='body2' sx={{ color: '#828C99', fontSize: 12, lineHeight: 1.2 }}>
            {props.label}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: props.color, lineHeight: 1.3 }}>
            {props.value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '-';
  return dayjs(value).format(`${ENV_CONFIG.config.DATE_FORMAT} HH:mm:ss`);
}

const GRID_SX = {
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: '#F5F7FA',
    borderBottom: '2px solid #E8ECF0',
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: 12, color: '#3B4797' },
  },
  '& .MuiDataGrid-row.odd': { bgcolor: '#FAFBFC' },
  '& .MuiDataGrid-row:hover': { bgcolor: '#F0F4FF' },
  '& .MuiDataGrid-cell': { fontSize: 12 },
};

function DatabaseGroupSelector(props: { selected: string[]; onToggle: (group: string) => void }) {
  return (
    <Stack spacing={1}>
      {DATABASE_GROUP_OPTIONS.map((group) => {
        const selected = props.selected.includes(group.value);
        return (
          <Paper
            key={group.value}
            variant='outlined'
            onClick={() => props.onToggle(group.value)}
            sx={{
              p: 1.5,
              cursor: 'pointer',
              borderRadius: 2,
              borderColor: selected ? '#3B4797' : '#E8ECF0',
              borderWidth: selected ? 2 : 1,
              bgcolor: selected ? alpha('#3B4797', 0.04) : 'transparent',
              transition: 'all 0.15s ease',
              '&:hover': {
                borderColor: selected ? '#3B4797' : '#B0B8C4',
                bgcolor: selected ? alpha('#3B4797', 0.04) : '#FAFBFC',
              },
            }}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: selected ? alpha('#3B4797', 0.1) : '#F3F6F9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {group.icon}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 13, color: '#141414' }}>
                  {group.label}
                </Typography>
                <Typography variant='body2' sx={{ fontSize: 11, color: '#828C99' }}>
                  {group.description}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: selected ? 'none' : '2px solid #CDD3DA',
                  bgcolor: selected ? '#3B4797' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                }}
              >
                {selected && <CheckCircleOutlineIcon sx={{ fontSize: 20, color: '#fff' }} />}
              </Box>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

// ── Migration Run Detail Dialog ──

function RunDetailDialog(props: { run: PlatformMigrationRun | null; open: boolean; onClose: () => void }) {
  const { run, open, onClose } = props;
  const runInProgress = run ? ['RUNNING', 'QUEUED'].includes(run.status) : false;
  const { data: items, isLoading, isError, error } = usePlatformMigrationItems(run?.runId || undefined, runInProgress);

  const progress =
    run && run.totalTargets > 0
      ? Math.round(((run.successfulTargets + run.failedTargets) / run.totalTargets) * 100)
      : 0;

  const columns = useMemo<GridColDef<PlatformMigrationRunItem>[]>(
    () => [
      { field: 'tenantName', headerName: 'Tenant Name', flex: 1, minWidth: 280 },
      {
        field: 'databaseGroup',
        headerName: 'Group',
        width: 180,
        renderCell: (params) => (
          <Typography variant='body2' sx={{ fontSize: 12, color: '#141414' }}>
            {params.value}
          </Typography>
        ),
      },
      { field: 'status', headerName: 'Status', width: 200, renderCell: (params) => <StatusChip status={params.value} /> },
      { field: 'contextsApplied', headerName: 'Contexts', width: 180, valueFormatter: (params) => (params.value as string[])?.join(', ') || '-' },
      {
        field: 'error',
        headerName: 'Error',
        flex: 1,
        minWidth: 300,
        renderCell: (params) =>
          params.value ? (
            <Typography variant='body2' sx={{ fontSize: 12, color: '#BB4942' }}>{params.value}</Typography>
          ) : (
            <Typography variant='body2' sx={{ fontSize: 12, color: '#828C99' }}>-</Typography>
          ),
      },
      { field: 'startedAt', headerName: 'Started', width: 180, valueFormatter: (params) => formatDateTime(params.value as string) },
      { field: 'finishedAt', headerName: 'Finished', width: 180, valueFormatter: (params) => formatDateTime(params.value as string) },
    ],
    []
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xl' fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='body1' fontWeight='bold'>Migration Run Details</Typography>
          <IconButton onClick={onClose} size='small' sx={{ color: '#98A2AE' }}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {run && (
          <Paper variant='outlined' sx={{ p: 2, mb: 2, mt: 1, borderRadius: 2, bgcolor: '#FAFBFC' }}>
            <Stack direction='row' spacing={3} alignItems='center' flexWrap='wrap' useFlexGap>
              <Box>
                <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Status</Typography>
                <StatusChip status={run.status} />
              </Box>
              <Box>
                <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Requested By</Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontSize: 13 }}>{run.requestedBy}</Typography>
              </Box>
              <Box>
                <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Database Groups</Typography>
                <Typography variant='body2' sx={{ fontSize: 13 }}>{run.databaseGroups?.join(', ')}</Typography>
              </Box>
              <Box>
                <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Started</Typography>
                <Typography variant='body2' sx={{ fontSize: 13 }}>{formatDateTime(run.startedAt)}</Typography>
              </Box>
              <Box sx={{ minWidth: 160 }}>
                <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11, mb: 0.5 }}>
                  Progress ({run.successfulTargets + run.failedTargets} / {run.totalTargets})
                </Typography>
                <LinearProgress
                  variant='determinate'
                  value={progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: '#E8ECF0',
                    '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: run.failedTargets > 0 ? '#E8833A' : '#42BB93' },
                  }}
                />
              </Box>
            </Stack>
          </Paper>
        )}
        {isError && <Alert severity='error' sx={{ mb: 2 }}>{getErrorMessage(error)}</Alert>}
        {isLoading && (
          <Stack height={300} justifyContent='center' alignItems='center'><ActivityIndicator /></Stack>
        )}
        {!isLoading && items?.data && (
          <Box sx={{ height: 460 }}>
            <DataGridPro
              rows={items.data}
              columns={columns}
              getRowId={(row) => row.itemId}
              density='compact'
              disableSelectionOnClick
              getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
              sx={GRID_SX}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant='outlined' size='small'>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Scope picker (tenant / system multi-select) ──

function ScopePicker(props: {
  label: string;
  placeholder: string;
  options: ScopeOption[];
  value: ScopeOption[];
  loading: boolean;
  onChange: (value: ScopeOption[]) => void;
}) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 12, color: '#141414', mb: 0.5 }}>
        {props.label}
      </Typography>
      <Autocomplete
        multiple
        size='small'
        loading={props.loading}
        options={props.options}
        value={props.value}
        onChange={(_, value) => props.onChange(value)}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disableCloseOnSelect
        renderInput={(params) => (
          <TextField {...params} placeholder={props.value.length === 0 ? props.placeholder : ''} />
        )}
      />
      <Typography variant='body2' sx={{ fontSize: 11, color: '#828C99', mt: 0.5 }}>
        Leave empty to include all.
      </Typography>
    </Box>
  );
}

// ── Migration Runs Tab ──

function MigrationRunsTab() {
  const { data: runs, isLoading, isError, error, refetch } = usePlatformMigrationRuns();
  const { mutateAsync: triggerMigration, isLoading: isTriggering } = useTriggerPlatformMigration();

  const [selectedRun, setSelectedRun] = useState<PlatformMigrationRun | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedTenants, setSelectedTenants] = useState<ScopeOption[]>([]);
  const [selectedSystems, setSelectedSystems] = useState<ScopeOption[]>([]);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const tenantGroupSelected = selectedGroups.includes(TENANT_DATABASES_GROUP);
  const systemGroupSelected = selectedGroups.includes(SYSTEM_DATABASES_GROUP);

  // Only fetch the pickers' options when the dialog is open and the group is selected.
  const { data: tenantOptionsData, isLoading: isLoadingTenants } = usePlatformMigrationTenantOptions(
    triggerDialogOpen && tenantGroupSelected,
  );
  const { data: systemOptionsData, isLoading: isLoadingSystems } = usePlatformMigrationSystemOptions(
    triggerDialogOpen && systemGroupSelected,
  );

  const tenantOptions = useMemo<ScopeOption[]>(
    () => (tenantOptionsData?.data?.data || []).map((t) => ({ id: t.id, label: `${t.tenantCode} — ${t.tenantName}` })),
    [tenantOptionsData],
  );
  const systemOptions = useMemo<ScopeOption[]>(
    () => (systemOptionsData?.data?.data || []).map((s) => ({ id: s.id, label: `${s.systemCode} — ${s.systemName}` })),
    [systemOptionsData],
  );

  const handleToggleGroup = (group: string) => {
    setSelectedGroups((prev) => {
      const next = prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group];
      // Clear scoped selections when their group is deselected.
      if (group === TENANT_DATABASES_GROUP && !next.includes(group)) setSelectedTenants([]);
      if (group === SYSTEM_DATABASES_GROUP && !next.includes(group)) setSelectedSystems([]);
      return next;
    });
  };

  const resetTriggerForm = () => {
    setSelectedGroups([]);
    setSelectedTenants([]);
    setSelectedSystems([]);
  };

  const closeTriggerDialog = () => {
    setTriggerDialogOpen(false);
    resetTriggerForm();
  };

  const handleTriggerMigration = async () => {
    try {
      const bodyRequest: PlatformMigrationRunRequest = { databaseGroups: selectedGroups };
      if (tenantGroupSelected && selectedTenants.length > 0) {
        bodyRequest.tenantIds = selectedTenants.map((t) => t.id);
      }
      if (systemGroupSelected && selectedSystems.length > 0) {
        bodyRequest.systemIds = selectedSystems.map((s) => s.id);
      }
      const result = await triggerMigration(bodyRequest);
      closeTriggerDialog();
      setSnackbar({ message: `Migration queued: ${result.data.message} (${result.data.totalTargets} targets)`, severity: 'success' });
      refetch();
    } catch (err: any) {
      setSnackbar({ message: getErrorMessage(err), severity: 'error' });
    }
  };

  const stats = useMemo(() => {
    const data = runs?.data || [];
    return {
      total: data.length,
      completed: data.filter((r) => r.status === 'COMPLETED').length,
      running: data.filter((r) => r.status === 'RUNNING' || r.status === 'QUEUED').length,
      failed: data.filter((r) => r.status === 'FAILED' || r.status === 'COMPLETED_WITH_FAILURES').length,
    };
  }, [runs]);

  const columns = useMemo<GridColDef<PlatformMigrationRun>[]>(
    () => [
      { field: 'status', headerName: 'Status', width: 220, renderCell: (params) => <StatusChip status={params.value} /> },
      { field: 'message', headerName: 'Message', flex: 1, minWidth: 280 },
      {
        field: 'databaseGroups',
        headerName: 'Database Groups',
        width: 250,
        renderCell: (params) => (
          <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
            {(params.value as string[])?.map((g) => (
              <Chip key={g} label={g.replace(/_/g, ' ')} size='small' sx={{ fontSize: 10, height: 20, bgcolor: '#F0F4FF', color: '#3B4797', fontWeight: 600 }} />
            ))}
          </Stack>
        ),
      },
      { field: 'requestedBy', headerName: 'Requested By', width: 200 },
      { field: 'totalTargets', headerName: 'Total', width: 70, type: 'number', headerAlign: 'center', align: 'center' },
      {
        field: 'successfulTargets',
        headerName: 'Success',
        width: 70,
        type: 'number',
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <Typography variant='body2' sx={{ fontSize: 12, fontWeight: 600, color: params.value > 0 ? '#42BB93' : '#828C99' }}>{params.value}</Typography>
        ),
      },
      {
        field: 'failedTargets',
        headerName: 'Failed',
        width: 70,
        type: 'number',
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <Typography variant='body2' sx={{ fontSize: 12, fontWeight: 600, color: params.value > 0 ? '#BB4942' : '#828C99' }}>{params.value}</Typography>
        ),
      },
      { field: 'startedAt', headerName: 'Started At', width: 170, valueFormatter: (params) => formatDateTime(params.value as string) },
      { field: 'finishedAt', headerName: 'Finished At', width: 170, valueFormatter: (params) => formatDateTime(params.value as string) },
    ],
    []
  );

  return (
    <>
      <Stack direction='row' justifyContent='flex-end' spacing={1} mb={2}>
        <Button
          variant='contained'
          size='small'
          startIcon={<RocketLaunchIcon />}
          onClick={() => setTriggerDialogOpen(true)}
          sx={{ textTransform: 'none', bgcolor: '#42BB93', '&:hover': { bgcolor: '#38A882' } }}
        >
          Run Migration
        </Button>
      </Stack>

      <Stack direction='row' spacing={2} mb={2}>
        <SummaryCard label='Total Runs' value={stats.total} color='#3B4797' bg='#F5F6FC' icon={<RocketLaunchIcon />} />
        <SummaryCard label='Completed' value={stats.completed} color='#42BB93' bg='#E8F8F2' icon={<CheckCircleOutlineIcon />} />
        <SummaryCard label='Running / Queued' value={stats.running} color='#00B3DC' bg='#E5F7FC' icon={<PlayCircleOutlineIcon />} />
        <SummaryCard label='Failed / Partial' value={stats.failed} color='#BB4942' bg='#FDECEB' icon={<ErrorOutlineIcon />} />
      </Stack>

      {isError && <Alert severity='error' sx={{ mb: 2 }}>{getErrorMessage(error)}</Alert>}

      <Paper variant='outlined' sx={{ borderRadius: 2 }}>
        <Box sx={{ height: 540 }}>
          <DataGridPro
            rows={runs?.data || []}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.runId}
            density='compact'
            disableSelectionOnClick
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
            onRowClick={(params) => {
              setSelectedRun(params.row);
              setDetailDialogOpen(true);
            }}
            sx={{ ...GRID_SX, cursor: 'pointer' }}
          />
        </Box>
      </Paper>

      <RunDetailDialog
        run={selectedRun}
        open={detailDialogOpen}
        onClose={() => { setDetailDialogOpen(false); setSelectedRun(null); }}
      />

      <Dialog open={triggerDialogOpen} onClose={closeTriggerDialog} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='body1' fontWeight='bold'>Trigger Platform Migration</Typography>
            <IconButton onClick={closeTriggerDialog} size='small' sx={{ color: '#98A2AE' }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2, color: '#828C99', fontSize: 13 }}>
            Select the database groups to include in this migration run:
          </Typography>
          <DatabaseGroupSelector selected={selectedGroups} onToggle={handleToggleGroup} />
          {tenantGroupSelected && (
            <ScopePicker
              label='Tenants (optional)'
              placeholder='All tenant databases'
              options={tenantOptions}
              value={selectedTenants}
              loading={isLoadingTenants}
              onChange={setSelectedTenants}
            />
          )}
          {systemGroupSelected && (
            <ScopePicker
              label='Systems (optional)'
              placeholder='All system databases'
              options={systemOptions}
              value={selectedSystems}
              loading={isLoadingSystems}
              onChange={setSelectedSystems}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeTriggerDialog} variant='outlined' size='small' sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant='contained'
            size='small'
            onClick={handleTriggerMigration}
            disabled={selectedGroups.length === 0 || isTriggering}
            startIcon={isTriggering ? <ActivityIndicator size={16} /> : <RocketLaunchIcon />}
            sx={{ textTransform: 'none', bgcolor: '#42BB93', '&:hover': { bgcolor: '#38A882' }, '&.Mui-disabled': { bgcolor: '#E8ECF0' } }}
          >
            {isTriggering ? 'Running...' : `Run Migration${selectedGroups.length > 0 ? ` (${selectedGroups.length})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {!!snackbar && (
        <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={() => setSnackbar(null)} autoHideDuration={5000}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant='filled'>{snackbar.message}</Alert>
        </Snackbar>
      )}
    </>
  );
}

// ── Health Check Tab ──

function HealthCheckTab(props: { onNavigateToSync: (healthCheckRunId: string) => void }) {
  const { mutateAsync: triggerHealthCheck, isLoading: isTriggering } = useTriggerHealthCheck();

  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [healthCheckRunId, setHealthCheckRunId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const { data: statusData, isLoading: isStatusLoading } = useHealthCheckStatus(healthCheckRunId || undefined);
  const healthCheckInProgress = statusData?.data ? ['RUNNING', 'QUEUED'].includes(statusData.data.status) : false;
  const { data: itemsData, isLoading: isItemsLoading } = useHealthCheckItems(healthCheckRunId || undefined, healthCheckInProgress);

  const healthCheckRun = statusData?.data;
  const healthCheckItems = itemsData?.data;

  const handleToggleGroup = (group: string) => {
    setSelectedGroups((prev) => (prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]));
  };

  const handleTrigger = async () => {
    try {
      const result = await triggerHealthCheck({ databaseGroups: selectedGroups });
      setTriggerDialogOpen(false);
      setSelectedGroups([]);
      setHealthCheckRunId(result.data.runId);
      setSnackbar({ message: `Health check queued: ${result.data.message} (${result.data.totalTargets} targets)`, severity: 'success' });
    } catch (err: any) {
      setSnackbar({ message: getErrorMessage(err), severity: 'error' });
    }
  };

  const columns = useMemo<GridColDef<HealthCheckItem>[]>(
    () => [
      { field: 'targetName', headerName: 'Target Name', flex: 1, minWidth: 240 },
      { field: 'databaseGroup', headerName: 'Group', width: 170 },
      { field: 'status', headerName: 'Status', width: 280, renderCell: (params) => <StatusChip status={params.value} /> },
      {
        field: 'matchedScenarioKeys',
        headerName: 'Scenarios',
        flex: 1,
        minWidth: 300,
        renderCell: (params) => {
          const keys = params.value as string[];
          if (!keys?.length) return <Typography variant='body2' sx={{ fontSize: 12, color: '#828C99' }}>-</Typography>;
          return (
            <Tooltip title={keys.join(', ')} arrow>
              <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap sx={{ overflow: 'hidden' }}>
                {keys.slice(0, 2).map((k) => (
                  <Chip key={k} label={k} size='small' sx={{ fontSize: 9, height: 18, bgcolor: '#FFF3E8', color: '#E8833A', fontWeight: 600 }} />
                ))}
                {keys.length > 2 && (
                  <Chip label={`+${keys.length - 2}`} size='small' sx={{ fontSize: 9, height: 18, bgcolor: '#F3F6F9', color: '#828C99' }} />
                )}
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: 'message',
        headerName: 'Message',
        flex: 1,
        minWidth: 250,
        renderCell: (params) => (
          <Tooltip title={params.value || ''} arrow>
            <Typography variant='body2' sx={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {params.value || '-'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'error',
        headerName: 'Error',
        width: 200,
        renderCell: (params) =>
          params.value ? (
            <Tooltip title={params.value} arrow>
              <Typography variant='body2' sx={{ fontSize: 12, color: '#BB4942', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {params.value}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant='body2' sx={{ fontSize: 12, color: '#828C99' }}>-</Typography>
          ),
      },
      { field: 'startedAt', headerName: 'Started', width: 170, valueFormatter: (params) => formatDateTime(params.value as string) },
      { field: 'finishedAt', headerName: 'Finished', width: 170, valueFormatter: (params) => formatDateTime(params.value as string) },
    ],
    []
  );

  const isRunComplete = healthCheckRun && (healthCheckRun.status === 'COMPLETED' || healthCheckRun.status === 'COMPLETED_WITH_FAILURES');

  return (
    <>
      <Stack direction='row' justifyContent='flex-end' spacing={1} mb={2}>
        <Button
          variant='contained'
          size='small'
          startIcon={<HealthAndSafetyIcon />}
          onClick={() => setTriggerDialogOpen(true)}
          sx={{ textTransform: 'none', bgcolor: '#00B3DC', '&:hover': { bgcolor: '#009BBF' } }}
        >
          Run Health Check
        </Button>
      </Stack>

      {/* Health Check Run Status */}
      {isStatusLoading && (
        <Stack height={200} justifyContent='center' alignItems='center'><ActivityIndicator /></Stack>
      )}

      {healthCheckRun && (
        <>
          <Stack direction='row' spacing={2} mb={2}>
            <SummaryCard label='Total Targets' value={healthCheckRun.totalTargets} color='#3B4797' bg='#F5F6FC' icon={<DnsIcon />} />
            <SummaryCard label='Healthy' value={healthCheckRun.healthyTargets} color='#42BB93' bg='#E8F8F2' icon={<VerifiedIcon />} />
            <SummaryCard label='Repairable' value={healthCheckRun.repairableTargets} color='#E8833A' bg='#FFF3E8' icon={<BuildCircleIcon />} />
            <SummaryCard label='Manual Review' value={healthCheckRun.manualReviewTargets} color='#BB4942' bg='#FDECEB' icon={<VisibilityIcon />} />
            <SummaryCard label='Unreachable' value={healthCheckRun.unreachableTargets} color='#6B7280' bg='#F3F4F6' icon={<SignalWifiOffIcon />} />
          </Stack>

          <Paper variant='outlined' sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#FAFBFC' }}>
            <Stack direction='row' spacing={3} alignItems='center' flexWrap='wrap' useFlexGap>
              <Box>
                <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Run ID</Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontSize: 12, fontFamily: 'monospace' }}>{healthCheckRun.runId.substring(0, 8)}...</Typography>
              </Box>
              <Box>
                <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Status</Typography>
                <StatusChip status={healthCheckRun.status} />
              </Box>
              <Box>
                <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Requested By</Typography>
                <Typography variant='body2' fontWeight={600} sx={{ fontSize: 13 }}>{healthCheckRun.requestedBy}</Typography>
              </Box>
              <Box>
                <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Started</Typography>
                <Typography variant='body2' sx={{ fontSize: 13 }}>{formatDateTime(healthCheckRun.startedAt)}</Typography>
              </Box>
              <Box>
                <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Finished</Typography>
                <Typography variant='body2' sx={{ fontSize: 13 }}>{formatDateTime(healthCheckRun.finishedAt)}</Typography>
              </Box>
              {isRunComplete && healthCheckRun.repairableTargets > 0 && (
                <Box sx={{ ml: 'auto' }}>
                  <Button
                    variant='contained'
                    size='small'
                    startIcon={<SyncIcon />}
                    onClick={() => props.onNavigateToSync(healthCheckRun.runId)}
                    sx={{ textTransform: 'none', bgcolor: '#42BB93', '&:hover': { bgcolor: '#38A882' } }}
                  >
                    Sync Repairable ({healthCheckRun.repairableTargets})
                  </Button>
                </Box>
              )}
            </Stack>
          </Paper>
        </>
      )}

      {/* Health Check Items Table */}
      {healthCheckRunId && !isStatusLoading && (
        <Paper variant='outlined' sx={{ borderRadius: 2 }}>
          <Box sx={{ height: 460 }}>
            <DataGridPro
              rows={healthCheckItems || []}
              columns={columns}
              loading={isItemsLoading}
              getRowId={(row) => row.itemId}
              density='compact'
              disableSelectionOnClick
              getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
              sx={GRID_SX}
            />
          </Box>
        </Paper>
      )}

      {!healthCheckRunId && !isStatusLoading && (
        <Paper variant='outlined' sx={{ borderRadius: 2, p: 6 }}>
          <Stack alignItems='center' spacing={2}>
            <HealthAndSafetyIcon sx={{ fontSize: 48, color: '#CDD3DA' }} />
            <Typography variant='body2' sx={{ color: '#828C99', fontSize: 14 }}>
              Run a health check to scan database targets for migration readiness
            </Typography>
            <Button
              variant='outlined'
              size='small'
              startIcon={<HealthAndSafetyIcon />}
              onClick={() => setTriggerDialogOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Run Health Check
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Trigger Dialog */}
      <Dialog open={triggerDialogOpen} onClose={() => setTriggerDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='body1' fontWeight='bold'>Run Health Check</Typography>
            <IconButton onClick={() => setTriggerDialogOpen(false)} size='small' sx={{ color: '#98A2AE' }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2, color: '#828C99', fontSize: 13 }}>
            Select the database groups to scan:
          </Typography>
          <DatabaseGroupSelector selected={selectedGroups} onToggle={handleToggleGroup} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTriggerDialogOpen(false)} variant='outlined' size='small' sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant='contained'
            size='small'
            onClick={handleTrigger}
            disabled={selectedGroups.length === 0 || isTriggering}
            startIcon={isTriggering ? <ActivityIndicator size={16} /> : <HealthAndSafetyIcon />}
            sx={{ textTransform: 'none', bgcolor: '#00B3DC', '&:hover': { bgcolor: '#009BBF' }, '&.Mui-disabled': { bgcolor: '#E8ECF0' } }}
          >
            {isTriggering ? 'Running...' : `Run Health Check${selectedGroups.length > 0 ? ` (${selectedGroups.length})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {!!snackbar && (
        <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={() => setSnackbar(null)} autoHideDuration={5000}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant='filled'>{snackbar.message}</Alert>
        </Snackbar>
      )}
    </>
  );
}

// ── Sync Tab ──

function SyncTab(props: { initialHealthCheckRunId?: string }) {
  const [healthCheckRunId, setHealthCheckRunId] = useState<string>(props.initialHealthCheckRunId || '');
  const [syncRunId, setSyncRunId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const { data: healthCheckStatus } = useHealthCheckStatus(healthCheckRunId || undefined);
  const { data: healthCheckItemsData } = useHealthCheckItems(healthCheckRunId || undefined);

  const { mutateAsync: triggerFleetSync, isLoading: isFleetSyncing } = useTriggerFleetSync();
  const { mutateAsync: triggerTenantSync, isLoading: isTenantSyncing } = useTriggerTenantSync();

  const { data: syncStatusData, isLoading: isSyncStatusLoading } = useSyncStatus(syncRunId || undefined);
  const syncInProgress = syncStatusData?.data ? ['RUNNING', 'QUEUED'].includes(syncStatusData.data.status) : false;
  const { data: syncItemsData, isLoading: isSyncItemsLoading } = useSyncItems(syncRunId || undefined, syncInProgress);

  const syncRun = syncStatusData?.data;
  const syncItems = syncItemsData?.data;
  const healthCheckRun = healthCheckStatus?.data;
  const healthCheckItems = healthCheckItemsData?.data;

  // Update healthCheckRunId if passed from parent
  useState(() => {
    if (props.initialHealthCheckRunId) setHealthCheckRunId(props.initialHealthCheckRunId);
  });

  const handleFleetSync = async () => {
    if (!healthCheckRunId) return;
    try {
      const result = await triggerFleetSync({ healthCheckRunId });
      setSyncRunId(result.data.runId);
      setSnackbar({ message: `Fleet sync queued: ${result.data.message} (${result.data.totalTargets} targets)`, severity: 'success' });
    } catch (err: any) {
      setSnackbar({ message: getErrorMessage(err), severity: 'error' });
    }
  };

  const handleTenantSync = useCallback(async (tenantId: string) => {
    if (!healthCheckRunId) return;
    try {
      const result = await triggerTenantSync({ healthCheckRunId, tenantId });
      setSyncRunId(result.data.runId);
      setSnackbar({ message: `Tenant sync queued: ${result.data.message}`, severity: 'success' });
    } catch (err: any) {
      setSnackbar({ message: getErrorMessage(err), severity: 'error' });
    }
  }, [healthCheckRunId, triggerTenantSync]);

  const repairableItems = useMemo(
    () => (healthCheckItems || []).filter((item) => item.status === 'REPAIRABLE_CHECKSUM_ONLY' || item.status === 'REPAIRABLE_PATCH_AND_CHECKSUM'),
    [healthCheckItems]
  );

  const healthCheckColumns = useMemo<GridColDef<HealthCheckItem>[]>(
    () => [
      { field: 'targetName', headerName: 'Target Name', flex: 1, minWidth: 240 },
      { field: 'databaseGroup', headerName: 'Group', width: 170 },
      { field: 'status', headerName: 'Status', width: 280, renderCell: (params) => <StatusChip status={params.value} /> },
      {
        field: 'matchedScenarioKeys',
        headerName: 'Scenarios',
        width: 220,
        renderCell: (params) => {
          const keys = params.value as string[];
          if (!keys?.length) return <Typography variant='body2' sx={{ fontSize: 12, color: '#828C99' }}>-</Typography>;
          return (
            <Tooltip title={keys.join(', ')} arrow>
              <Typography variant='body2' sx={{ fontSize: 11 }}>{keys.length} scenario{keys.length > 1 ? 's' : ''}</Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const isRepairable = row.status === 'REPAIRABLE_CHECKSUM_ONLY' || row.status === 'REPAIRABLE_PATCH_AND_CHECKSUM';
          if (!isRepairable) return null;
          return (
            <Button
              variant='outlined'
              size='small'
              startIcon={<SyncIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                handleTenantSync(row.targetRecordId);
              }}
              disabled={isTenantSyncing}
              sx={{ textTransform: 'none', fontSize: 11, height: 26 }}
            >
              Sync
            </Button>
          );
        },
      },
    ],
    [isTenantSyncing, handleTenantSync]
  );

  const syncColumns = useMemo<GridColDef<SyncItem>[]>(
    () => [
      { field: 'targetName', headerName: 'Target Name', flex: 1, minWidth: 240 },
      { field: 'databaseGroup', headerName: 'Group', width: 170 },
      { field: 'status', headerName: 'Status', width: 220, renderCell: (params) => <StatusChip status={params.value} /> },
      {
        field: 'matchedScenarioKeys',
        headerName: 'Scenarios',
        width: 220,
        renderCell: (params) => {
          const keys = params.value as string[];
          if (!keys?.length) return <Typography variant='body2' sx={{ fontSize: 12, color: '#828C99' }}>-</Typography>;
          return (
            <Tooltip title={keys.join(', ')} arrow>
              <Typography variant='body2' sx={{ fontSize: 11 }}>{keys.length} scenario{keys.length > 1 ? 's' : ''}</Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'message',
        headerName: 'Message',
        flex: 1,
        minWidth: 250,
        renderCell: (params) => (
          <Tooltip title={params.value || ''} arrow>
            <Typography variant='body2' sx={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {params.value || '-'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'error',
        headerName: 'Error',
        width: 200,
        renderCell: (params) =>
          params.value ? (
            <Tooltip title={params.value} arrow>
              <Typography variant='body2' sx={{ fontSize: 12, color: '#BB4942', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {params.value}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant='body2' sx={{ fontSize: 12, color: '#828C99' }}>-</Typography>
          ),
      },
      { field: 'startedAt', headerName: 'Started', width: 170, valueFormatter: (params) => formatDateTime(params.value as string) },
      { field: 'finishedAt', headerName: 'Finished', width: 170, valueFormatter: (params) => formatDateTime(params.value as string) },
    ],
    []
  );

  return (
    <>
      {/* Health Check Run ID input */}
      {!healthCheckRunId && (
        <Paper variant='outlined' sx={{ borderRadius: 2, p: 6 }}>
          <Stack alignItems='center' spacing={2}>
            <SyncIcon sx={{ fontSize: 48, color: '#CDD3DA' }} />
            <Typography variant='body2' sx={{ color: '#828C99', fontSize: 14, textAlign: 'center' }}>
              Run a Health Check first, then use the "Sync Repairable" button to start syncing
            </Typography>
          </Stack>
        </Paper>
      )}

      {healthCheckRunId && (
        <>
          {/* Sync action bar */}
          <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
            <Box>
              <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Health Check Run</Typography>
              <Typography variant='body2' fontWeight={600} sx={{ fontSize: 13, fontFamily: 'monospace' }}>
                {healthCheckRunId.substring(0, 8)}...
                {healthCheckRun && (
                  <StatusChip status={healthCheckRun.status} />
                )}
              </Typography>
            </Box>
            <Stack direction='row' spacing={1}>
              <Button
                variant='contained'
                size='small'
                startIcon={isFleetSyncing ? <ActivityIndicator size={16} /> : <SyncIcon />}
                onClick={handleFleetSync}
                disabled={isFleetSyncing || repairableItems.length === 0}
                sx={{ textTransform: 'none', bgcolor: '#42BB93', '&:hover': { bgcolor: '#38A882' }, '&.Mui-disabled': { bgcolor: '#E8ECF0' } }}
              >
                {isFleetSyncing ? 'Syncing...' : `Fleet Sync (${repairableItems.length} targets)`}
              </Button>
            </Stack>
          </Stack>

          {/* Repairable targets from health check */}
          {!syncRunId && (
            <>
              <Typography variant='body2' fontWeight={600} sx={{ mb: 1, fontSize: 13, color: '#141414' }}>
                Repairable Targets ({repairableItems.length})
              </Typography>
              <Paper variant='outlined' sx={{ borderRadius: 2 }}>
                <Box sx={{ height: 400 }}>
                  <DataGridPro
                    rows={repairableItems}
                    columns={healthCheckColumns}
                    getRowId={(row) => row.itemId}
                    density='compact'
                    disableSelectionOnClick
                    getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                    sx={GRID_SX}
                  />
                </Box>
              </Paper>
            </>
          )}

          {/* Sync Run Status & Items */}
          {syncRunId && (
            <>
              {isSyncStatusLoading && (
                <Stack height={200} justifyContent='center' alignItems='center'><ActivityIndicator /></Stack>
              )}

              {syncRun && (
                <>
                  <Stack direction='row' spacing={2} mb={2}>
                    <SummaryCard label='Total Targets' value={syncRun.totalTargets} color='#3B4797' bg='#F5F6FC' icon={<DnsIcon />} />
                    <SummaryCard label='Completed' value={syncRun.completedTargets} color='#42BB93' bg='#E8F8F2' icon={<CheckCircleOutlineIcon />} />
                    <SummaryCard label='Failed' value={syncRun.failedTargets} color='#BB4942' bg='#FDECEB' icon={<ErrorOutlineIcon />} />
                    <SummaryCard label='Skipped' value={syncRun.skippedTargets} color='#828C99' bg='#F3F6F9' icon={<RemoveCircleOutlineIcon />} />
                  </Stack>

                  <Paper variant='outlined' sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#FAFBFC' }}>
                    <Stack direction='row' spacing={3} alignItems='center' flexWrap='wrap' useFlexGap>
                      <Box>
                        <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Status</Typography>
                        <StatusChip status={syncRun.status} />
                      </Box>
                      <Box>
                        <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Requested By</Typography>
                        <Typography variant='body2' fontWeight={600} sx={{ fontSize: 13 }}>{syncRun.requestedBy}</Typography>
                      </Box>
                      <Box>
                        <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Started</Typography>
                        <Typography variant='body2' sx={{ fontSize: 13 }}>{formatDateTime(syncRun.startedAt)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11 }}>Finished</Typography>
                        <Typography variant='body2' sx={{ fontSize: 13 }}>{formatDateTime(syncRun.finishedAt)}</Typography>
                      </Box>
                      <Box sx={{ minWidth: 160 }}>
                        <Typography variant='body2' sx={{ color: '#828C99', fontSize: 11, mb: 0.5 }}>
                          Progress ({syncRun.completedTargets + syncRun.failedTargets + syncRun.skippedTargets} / {syncRun.totalTargets})
                        </Typography>
                        <LinearProgress
                          variant='determinate'
                          value={syncRun.totalTargets > 0 ? Math.round(((syncRun.completedTargets + syncRun.failedTargets + syncRun.skippedTargets) / syncRun.totalTargets) * 100) : 0}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: '#E8ECF0',
                            '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: syncRun.failedTargets > 0 ? '#E8833A' : '#42BB93' },
                          }}
                        />
                      </Box>
                    </Stack>
                  </Paper>
                </>
              )}

              <Paper variant='outlined' sx={{ borderRadius: 2 }}>
                <Box sx={{ height: 400 }}>
                  <DataGridPro
                    rows={syncItems || []}
                    columns={syncColumns}
                    loading={isSyncItemsLoading}
                    getRowId={(row) => row.itemId}
                    density='compact'
                    disableSelectionOnClick
                    getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                    sx={GRID_SX}
                  />
                </Box>
              </Paper>
            </>
          )}
        </>
      )}

      {!!snackbar && (
        <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={() => setSnackbar(null)} autoHideDuration={5000}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant='filled'>{snackbar.message}</Alert>
        </Snackbar>
      )}
    </>
  );
}

// ── Main Page ──

export default function PlatformMigrationPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [syncHealthCheckRunId, setSyncHealthCheckRunId] = useState<string | undefined>();

  const handleNavigateToSync = (healthCheckRunId: string) => {
    setSyncHealthCheckRunId(healthCheckRunId);
    setActiveTab(2);
  };

  return (
    <TenantHomeDashboardNavigationBar>
      <Stack spacing={2} p={3}>
        {/* Header */}
        <Box>
          <Typography variant='body1' fontWeight='bold'>
            Platform Migration
          </Typography>
          <Typography variant='body2' sx={{ color: '#828C99', fontSize: 12 }}>
            Manage and monitor database migration, health checks, and sync operations
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper variant='outlined' sx={{ borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              px: 2,
              borderBottom: '1px solid #E8ECF0',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                minHeight: 44,
                color: '#828C99',
                '&.Mui-selected': { color: '#3B4797' },
              },
              '& .MuiTabs-indicator': { bgcolor: '#3B4797', height: 3, borderRadius: '3px 3px 0 0' },
            }}
          >
            <Tab icon={<RocketLaunchIcon sx={{ fontSize: 18 }} />} iconPosition='start' label='Migration Runs' />
            <Tab icon={<HealthAndSafetyIcon sx={{ fontSize: 18 }} />} iconPosition='start' label='Health Check' />
            <Tab icon={<SyncIcon sx={{ fontSize: 18 }} />} iconPosition='start' label='Sync' />
          </Tabs>

          <Box p={2}>
            {activeTab === 0 && <MigrationRunsTab />}
            {activeTab === 1 && <HealthCheckTab onNavigateToSync={handleNavigateToSync} />}
            {activeTab === 2 && <SyncTab initialHealthCheckRunId={syncHealthCheckRunId} />}
          </Box>
        </Paper>
      </Stack>
    </TenantHomeDashboardNavigationBar>
  );
}
